import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowRight, CaretRight, Check, CircleNotch, ClosedCaptioning, Download, FilmStrip, FolderOpen, Lightning, Microphone, Sparkle, SpeakerHigh, Star, Trash, Upload, VideoCamera } from "@phosphor-icons/react";
import { API, BACKEND_URL } from "@/lib/api";
import RenderBanner from "@/components/RenderBanner";
import PlanUsagePill from "@/components/PlanUsagePill";
import PublishPanel from "@/components/PublishPanel";

const CAPTION_THEMES = [
  { id: "Hormozi_1", name: "Hormozi Bold", font: "800", fontFamily: "'Anton', sans-serif", color: "#FFFFFF", highlight: "#FFD700", bg: "#000", stroke: true, strokeColor: "#000", transform: "uppercase", desc: "Bold all-caps, yellow highlight" },
  { id: "Hormozi_2", name: "Hormozi Clean", font: "700", fontFamily: "'Inter', sans-serif", color: "#FFFFFF", highlight: "#4ADE80", bg: "#1a1a1a", stroke: false, transform: "uppercase", desc: "Clean white, green highlight" },
  { id: "Hormozi_3", name: "Hormozi Shadow", font: "900", fontFamily: "'Inter', sans-serif", color: "#FFFFFF", highlight: "#E91E8C", bg: "#111", stroke: false, shadow: true, transform: "uppercase", desc: "Bold with shadow + pink pop" },
  { id: "Beast", name: "Beast", font: "900", fontFamily: "'Comic Sans MS', cursive", color: "#FFFF00", highlight: "#FF0000", bg: "#000", stroke: true, strokeColor: "#000", transform: "uppercase", desc: "Comic-style, yellow/red dynamic" },
  { id: "Ali", name: "Ali", font: "500", fontFamily: "'Inter', sans-serif", color: "#1a1a1a", highlight: "#E5E5E5", bg: "#F5F5F5", stroke: false, transform: "none", desc: "Black text on light minimal bg" },
  { id: "Noah", name: "Noah", font: "600", fontFamily: "'Georgia', serif", color: "#FFFFFF", highlight: "#8B5CF6", bg: "#1a1a1a", stroke: false, shadow: true, transform: "none", fontStyle: "italic", desc: "Italic serif, purple drop shadow" },
  { id: "Karl", name: "Karl", font: "800", fontFamily: "'Inter', sans-serif", color: "#FFFFFF", highlight: "#00D4FF", bg: "#111", stroke: true, strokeColor: "#00D4FF", transform: "uppercase", desc: "White text, cyan border outlines" },
  { id: "Luke", name: "Luke", font: "900", fontFamily: "'Inter', sans-serif", color: "#FF4444", highlight: "#FFD700", bg: "#000", stroke: true, strokeColor: "#FFF", transform: "uppercase", desc: "Neon red/gold, high energy" },
  { id: "Devin", name: "Devin", font: "800", fontFamily: "'Inter', sans-serif", color: "#FF6EC7", highlight: "#7C3AED", bg: "#0f0f23", stroke: false, transform: "uppercase", desc: "Pink/purple, rotating scale" },
  { id: "Celine", name: "Celine", font: "400", fontFamily: "'Georgia', serif", color: "#FFFFFF", highlight: "#D4A574", bg: "#2a2a2a", stroke: false, shadow: true, transform: "none", desc: "Elegant serif, soft gold shadow" },
  { id: "Maya", name: "Maya", font: "600", fontFamily: "'Georgia', serif", color: "#FFD700", highlight: "#FFA500", bg: "#1a0a00", stroke: false, shadow: true, glow: true, transform: "none", desc: "Gold serif with warm glow" },
  { id: "Ella", name: "Ella", font: "300", fontFamily: "'Inter', sans-serif", color: "#C4B5FD", highlight: "#818CF8", bg: "#1e1b4b", stroke: false, transform: "none", desc: "Soft lavender, blur animations" },
  { id: "Dan", name: "Dan", font: "900", fontFamily: "'Impact', sans-serif", color: "#FFFFFF", highlight: "#FF0000", bg: "#000", stroke: true, strokeColor: "#000", transform: "uppercase", desc: "Huge uppercase, white/red impact" },
  { id: "David", name: "David", font: "900", fontFamily: "'Inter', sans-serif", color: "#FFFFFF", highlight: "#FFD700", bg: "#111", stroke: true, strokeColor: "#333", transform: "uppercase", desc: "Bold uppercase, gold scaling" },
  { id: "Tracy", name: "Tracy", font: "400", fontFamily: "'Inter', sans-serif", color: "#FFFFFF", highlight: "#FFFFFF", bg: "#333", stroke: false, shadow: true, glow: true, transform: "none", desc: "Minimal white with soft glow" },
  { id: "Umi", name: "Umi", font: "300", fontFamily: "'Georgia', serif", color: "#FFFFFF", highlight: "#9CA3AF", bg: "#1f1f1f", stroke: false, transform: "none", fontStyle: "italic", desc: "Thin italic, letter-by-letter" },
  { id: "Iman", name: "Iman", font: "600", fontFamily: "'Inter', sans-serif", color: "#FFFFFF", highlight: "#FFFFFF", bg: "#444", stroke: true, strokeColor: "#000", transform: "none", desc: "Clean white, black border" },
  { id: "William", name: "William", font: "700", fontFamily: "'Inter', sans-serif", color: "#00FF88", highlight: "#FF6B35", bg: "#0a0a0a", stroke: false, transform: "uppercase", desc: "Green/orange alternating" },
];

// ============= AUTO CREATE SECTION =============
const AutoCreate = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("tiktok");
  const [length, setLength] = useState("15sec");
  const [tone, setTone] = useState("gossip-wisdom");
  const [angle, setAngle] = useState("");
  const [customScript, setCustomScript] = useState("");
  const [useOwnScript, setUseOwnScript] = useState(false);
  // "Generate & Review Script" flow — lets the user read/edit the AI script
  // before spending render credits (opt-in; one-click create stays as-is).
  const [reviewScript, setReviewScript] = useState("");
  const [reviewCaption, setReviewCaption] = useState("");
  const [reviewHashtags, setReviewHashtags] = useState("");
  const [reviewMode, setReviewMode] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [outfitFile, setOutfitFile] = useState(null);
  const [outfitPreview, setOutfitPreview] = useState(null);
  const [outfitChangedUrl, setOutfitChangedUrl] = useState(null);
  const [changingOutfit, setChangingOutfit] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [creating, setCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [includeCaptions, setIncludeCaptions] = useState(false);
  const [captionTheme, setCaptionTheme] = useState("Hormozi_1");
  const [includeBroll, setIncludeBroll] = useState(false);
  const [skipLipsync, setSkipLipsync] = useState(false);
  const [talkingPhotoMode, setTalkingPhotoMode] = useState("realistic"); // "realistic" (max likeness) is the platform default. "prompted" is the faster mode.
  // Provider preference for talking-photo render.
  //   "auto"   → Pruna (talking-photo) / VEED (lip-sync) — best value (DEFAULT)
  //   "pruna"  → Replicate Pruna (talking-photo)
  //   "heygen" → HeyGen HD premium (talking-photo + lip-sync)
  //   "veed"   → VEED Lipsync (lip-sync)
  // Fabric + Magic Hour retired.
  const [talkingPhotoProvider, setTalkingPhotoProvider] = useState("auto");
  const [talkingPhotoResolution, setTalkingPhotoResolution] = useState("480p");
  const [heygenWallet, setHeygenWallet] = useState(null); // {configured, balance, low}
  const [baseVideos, setBaseVideos] = useState([]);
  const [selectedBaseVideoId, setSelectedBaseVideoId] = useState(null); // null → Talking Photo; id → Lip Sync
  const [visualHook, setVisualHook] = useState(false);
  const [dynamicFraming, setDynamicFraming] = useState(false);
  const [lifestyleJobs, setLifestyleJobs] = useState([]);
  const [generatingIdle, setGeneratingIdle] = useState(false);

  // ---- Subscription/plan state (from GET /api/plans/me) — powers the
  // usage pill above the Generate button and the "you've hit your cap"
  // upgrade CTA. Admin users bypass every check.
  const [planInfo, setPlanInfo] = useState(null);
  const fetchPlanInfo = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plans/me`);
      setPlanInfo(res.data);
    } catch (e) { /* non-fatal — pill just won't render */ }
  }, []);
  useEffect(() => { fetchPlanInfo(); }, [fetchPlanInfo]);

  // HeyGen wallet guardrail — probe balance when the HD engine is selected
  // so we can nudge a top-up before a premium render fails mid-way.
  useEffect(() => {
    if (talkingPhotoProvider !== "heygen") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/heygen/wallet`);
        if (!cancelled) setHeygenWallet(res.data);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [talkingPhotoProvider]);

  // ---- Voice picker state (unified with ScriptStudio) ----
  // "openai" = free stock voices via Emergent key. "elevenlabs" = BYOK cloned.
  const [voiceProvider, setVoiceProvider] = useState("openai");
  const [openaiVoice, setOpenaiVoice] = useState("nova");
  const [openaiVoices, setOpenaiVoices] = useState([]);
  const [elevenlabsVoice, setElevenlabsVoice] = useState("");
  const [elevenlabsVoices, setElevenlabsVoices] = useState([]);
  const [elevenlabsAvailable, setElevenlabsAvailable] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  const [voiceFavorites, setVoiceFavorites] = useState([]); // ["openai:nova", ...]

  const FALLBACK_TOPICS = [
    "Why most brands fail in their first year",
    "The real reason people aren't engaging with your content",
    "How I built my brand from zero to six figures",
    "Stop copying other creators - here's what to do instead",
    "The morning routine that changed my business",
    "What nobody tells you about going viral",
    "How to build a personal brand on LinkedIn",
    "The #1 mistake new entrepreneurs make on social media",
    "Why your brand story matters more than your product",
    "How to turn followers into paying clients",
  ];

  const [personalizedTopics, setPersonalizedTopics] = useState([]);
  const [topicObjects, setTopicObjects] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(true);
  const [topicsRefreshing, setTopicsRefreshing] = useState(false);

  const fetchImages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ai-twin/images`);
      setImages(res.data.images || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auto-create/jobs`);
      setJobs(res.data.jobs || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchBaseVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/base-videos`);
      const vids = res.data.videos || [];
      setBaseVideos(vids);
      // Auto-select default if user has one and they haven't picked yet
      const dflt = vids.find((v) => v.is_default && v.status !== "processing" && v.status !== "failed");
      if (dflt && selectedBaseVideoId === null) {
        setSelectedBaseVideoId(dflt.id);
      }
    } catch (e) { /* non-fatal */ }
  }, [selectedBaseVideoId]);

  const fetchLifestyleJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/lifestyle/jobs`);
      const completed = (res.data.jobs || []).filter((j) => j.status === "completed" && j.video_url);
      setLifestyleJobs(completed);
    } catch (e) { /* non-fatal */ }
  }, []);

  // ---- Voice picker loaders / helpers (mirrors ScriptStudio) ----
  const loadVoiceData = useCallback(async () => {
    try {
      const [voices, providers, pref] = await Promise.all([
        axios.get(`${API}/voice/openai/voices`),
        axios.get(`${API}/voice/providers`),
        axios.get(`${API}/voice/preference`).catch(() => ({ data: {} })),
      ]);
      setOpenaiVoices(voices.data?.voices || []);
      const ell = (providers.data?.providers || []).find((p) => p.id === "elevenlabs");
      setElevenlabsAvailable(Boolean(ell?.available));
      if (ell?.available) {
        const elVoices = await axios.get(`${API}/voice/elevenlabs/voices`);
        setElevenlabsVoices(elVoices.data?.voices || []);
      }
      // Hydrate user's saved default voice + favorites
      if (pref.data?.provider) setVoiceProvider(pref.data.provider);
      if (pref.data?.voice_id) {
        if (pref.data.provider === "elevenlabs") setElevenlabsVoice(pref.data.voice_id);
        else setOpenaiVoice(pref.data.voice_id);
      }
      setVoiceFavorites(pref.data?.favorites || []);
    } catch (e) {
      console.warn("Voice provider load failed:", e);
    }
  }, []);

  const persistVoicePref = useCallback(async (provider, voiceId) => {
    if (!voiceId) return;
    try {
      await axios.post(`${API}/voice/preference`, { provider, voice_id: voiceId });
    } catch (e) {
      console.warn("Could not save voice preference:", e);
    }
  }, []);

  const toggleVoiceFavorite = async (provider, voiceId, e) => {
    e?.stopPropagation();
    const key = `${provider}:${voiceId}`;
    const isFav = voiceFavorites.includes(key);
    const next = isFav ? voiceFavorites.filter((k) => k !== key) : [...voiceFavorites, key];
    setVoiceFavorites(next); // optimistic
    try {
      await axios.post(`${API}/voice/favorites`, { provider, voice_id: voiceId, favorite: !isFav });
    } catch (err) {
      setVoiceFavorites(voiceFavorites); // rollback
    }
  };

  const previewVoice = async (voiceId) => {
    if (previewingVoice === voiceId) return;
    setPreviewingVoice(voiceId);
    try {
      const res = await axios.post(
        `${API}/voice/openai/preview`,
        { voice_id: voiceId },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      a.onended = () => { setPreviewingVoice(null); URL.revokeObjectURL(url); };
      a.play();
    } catch (e) {
      setPreviewingVoice(null);
    }
  };

  const fetchTopics = useCallback(async (refresh = false) => {
    if (refresh) setTopicsRefreshing(true); else setTopicsLoading(true);
    try {
      const res = await axios.get(`${API}/topics/personalized${refresh ? "?refresh=true" : ""}`);
      setPersonalizedTopics(res.data.topics || []);
      setTopicObjects(res.data.topic_objects || []);
    } catch (e) {
      console.error(e);
      setPersonalizedTopics(FALLBACK_TOPICS);
      setTopicObjects([]);
    } finally {
      setTopicsLoading(false);
      setTopicsRefreshing(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => {
      fetchImages();
      fetchJobs();
      fetchTopics();
      fetchBaseVideos();
      fetchLifestyleJobs();
      loadVoiceData();
    }, 0);
    return () => clearTimeout(t);
  }, [fetchImages, fetchJobs, fetchTopics, fetchBaseVideos, fetchLifestyleJobs, loadVoiceData]);

  const handleGenerateIdle = async () => {
    if (!selectedImage) {
      setError("Pick an AI Twin first so we can generate an idle clip from it.");
      return;
    }
    setGeneratingIdle(true);
    try {
      const res = await axios.post(`${API}/base-videos/generate-idle`, { image_id: selectedImage.id });
      await fetchBaseVideos();
      const videoId = res.data.video.id;
      for (let i = 0; i < 48; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data } = await axios.get(`${API}/base-videos`);
        const v = (data.videos || []).find((x) => x.id === videoId);
        if (!v) break;
        setBaseVideos(data.videos || []);
        if (v.status === "ready") { setSelectedBaseVideoId(videoId); break; }
        if (v.status === "failed") { setError(v.error || "Idle clip generation failed"); break; }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate idle clip");
    } finally {
      setGeneratingIdle(false);
    }
  };

  const handleReuseLifestyle = async (jobId) => {
    try {
      const res = await axios.post(`${API}/base-videos/from-lifestyle/${jobId}`);
      await fetchBaseVideos();
      setSelectedBaseVideoId(res.data.video.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reuse lifestyle video");
    }
  };

  const handleReuseAutoCreate = async (jobId) => {
    try {
      const res = await axios.post(`${API}/base-videos/from-auto-create/${jobId}`);
      await fetchBaseVideos();
      setSelectedBaseVideoId(res.data.video.id);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to reuse Auto Create video");
    }
  };

  const handleOutfitDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setOutfitFile(file);
      setOutfitPreview(URL.createObjectURL(file));
      setOutfitChangedUrl(null);
    }
  };

  const previewScriptForReview = async () => {
    if ((!topic || topic === "custom") && !(useOwnScript && customScript.trim())) return;
    setPreviewing(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/auto-create/preview-script`, {
        topic: topic || "",
        platform,
        length,
        tone,
        angle,
        custom_script: useOwnScript ? customScript : null,
      });
      setReviewScript(res.data.script || "");
      setReviewCaption(res.data.caption || "");
      setReviewHashtags(res.data.hashtags || "");
      setReviewMode(true);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not generate a script to review. Please try again.";
      setError(String(detail));
    } finally {
      setPreviewing(false);
    }
  };

  const startAutoCreate = async (scriptOverride = null) => {
    if (!selectedImage) {
      setError("Please select an AI Twin image before rendering.");
      return;
    }
    if (scriptOverride == null && !topic) return;
    setCreating(true);
    setError(null);
    setResult(null);
    setCurrentStep((scriptOverride != null || useOwnScript) ? "voice" : "script");

    try {
      // Auto-apply outfit if file uploaded but not yet applied
      let finalOutfitUrl = outfitChangedUrl;
      if (outfitFile && !outfitChangedUrl) {
        setChangingOutfit(true);
        try {
          const formData = new FormData();
          formData.append("file", outfitFile);
          const uploadRes = await axios.post(`${API}/lifestyle/upload-outfit`, formData);
          const garmentUrl = uploadRes.data.url;
          const dressRes = await axios.post(`${API}/lifestyle/outfit-change?model_image_id=${selectedImage.id}&garment_url=${encodeURIComponent(garmentUrl)}`);
          finalOutfitUrl = dressRes.data.image_url;
          setOutfitChangedUrl(finalOutfitUrl);
        } catch (e) {
          console.error("Outfit change failed:", e);
        } finally {
          setChangingOutfit(false);
        }
      }

      const res = await axios.post(`${API}/auto-create`, {
        topic,
        platform,
        length,
        tone,
        angle,
        image_id: selectedImage.id,
        custom_script: scriptOverride != null ? scriptOverride : (useOwnScript ? customScript : null),
        outfit_image_url: finalOutfitUrl || null,
        include_captions: includeCaptions,
        caption_theme: captionTheme,
        include_broll: includeBroll,
        visual_hook: visualHook,
        dynamic_framing: dynamicFraming,
        skip_lipsync: skipLipsync,
        talking_photo_mode: talkingPhotoMode,
        talking_photo_provider: talkingPhotoProvider,
        talking_photo_resolution: talkingPhotoResolution,
        base_video_id: selectedBaseVideoId,
        // Per-request voice override — backend falls back to saved default if null.
        voice_provider: voiceProvider,
        voice_id: voiceProvider === "openai" ? openaiVoice : elevenlabsVoice,
      });

      setCurrentStep("voice");
      setResult(res.data);
      setReviewMode(false);
      fetchJobs(); // refresh jobs so banner picks up the new active job immediately
      pollJobProgress(res.data.job_id);
    } catch (e) {
      console.error("Auto-create error:", e);
      const status = e.response?.status;
      const detail = e.response?.data?.detail || "";
      const isAuthErr = status === 401 || detail.includes("Not authenticated");
      const safeMsg = isAuthErr
        ? "Your session has expired. Please log in again."
        : (detail ? String(detail) : "Something went wrong. Please try again.");
      setError(safeMsg);
      setCreating(false);
      setCurrentStep(null);
      if (isAuthErr) {
        window.location.assign("/login");
      }
    }
  };

  const pollJobProgress = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/auto-create/jobs/${jobId}`);
        const job = res.data;
        
        // Also refresh the jobs list so the render banner tracks current_step/elapsed time live
        fetchJobs();

        // Update step from backend
        if (job.current_step) {
          setCurrentStep(job.current_step === "done" ? "done" : job.current_step);
        }
        
        if (job.status === "completed") {
          setResult((prev) => ({ ...prev, output_url: job.output_url || job.sadtalker_url, status: "completed", video_provider: job.video_provider || null }));
          setCreating(false);
          setCurrentStep("done");
          fetchJobs();
          clearInterval(interval);
        } else if (job.status === "failed") {
          setError(job.error || "Content creation failed");
          setCreating(false);
          setCurrentStep(null);
          fetchJobs();
          clearInterval(interval);
        } else if ((job.status === "syncing" || job.status === "captioning" || job.status === "broll") && job.output_url) {
          setResult((prev) => ({ ...prev, output_url: job.output_url, status: "completed" }));
          setCreating(false);
          setCurrentStep("done");
          fetchJobs();
          clearInterval(interval);
        }
      } catch (e) {
        console.error("Polling error:", e);
      }
    }, 4000);
  };

  const deleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/auto-create/jobs/${jobId}`);
      setJobs((prev) => prev.filter((j) => j.id !== jobId));
    } catch (e) { console.error(e); }
  };

  const [expandedJob, setExpandedJob] = useState(null);

  const saveJobToVault = async (job) => {
    try {
      await axios.post(`${API}/vault/save`, {
        title: job.topic,
        platform: job.platform || "tiktok",
        category: "general",
        video_url: job.output_url,
        caption: job.caption || "",
        hashtags: job.hashtags || "",
        tags: "",
        post_type: "reel",
        source_job_id: job.id,
      });
      alert("Saved to Content Vault!");
    } catch (e) {
      console.error(e);
      alert("Error saving to vault");
    }
  };

  const STEPS = (() => {
    const base = [
      { key: "script", label: "Writing Script", icon: Microphone },
      { key: "voice", label: "Cloning Voice", icon: SpeakerHigh },
      { key: "video", label: "Generating Video", icon: VideoCamera },
    ];
    if (!skipLipsync) base.push({ key: "sync", label: "Lip-Syncing", icon: Sparkle });
    if (includeBroll) base.push({ key: "broll", label: "Adding B-Roll", icon: FilmStrip });
    if (includeCaptions) base.push({ key: "captions", label: "Adding Captions", icon: ClosedCaptioning });
    base.push({ key: "done", label: "Complete", icon: Check });
    // Assign percentages
    const total = base.length;
    return base.map((s, i) => ({ ...s, pct: Math.round(((i + 1) / total) * 100) }));
  })();

  const ALL_STEPS = [
    { key: "script", pct: 10 }, { key: "voice", pct: 25 }, { key: "video", pct: 40 },
    { key: "sync", pct: 55 }, { key: "broll", pct: 70 }, { key: "captions", pct: 85 }, { key: "done", pct: 100 },
  ];
  const getJobPct = (step) => ALL_STEPS.find(s => s.key === step)?.pct || 15;

  return (
    <div className="space-y-6" data-testid="auto-create-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-zinc-900">Auto Create</h2>
          <p className="text-sm text-zinc-500 mt-1">One-click content: Script + Voice + Video + Lip-Sync</p>
        </div>
        <PlanUsagePill />
      </div>

      {/* Active render banner with live countdown + "you can close the tab" reassurance */}
      <RenderBanner jobs={jobs} currentStep={currentStep} onJobCancelled={() => { setCurrentStep(null); setResult(null); fetchJobs(); }} />

      {/* Step 1: Select AI Twin — full-width horizontal strip so all twins
          are visible side-by-side without competing with the right column. */}
      <div className="card p-4" data-testid="autocreate-twin-picker">
        <div className="flex items-center justify-between mb-3">
          <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">1. Select Your AI Twin</label>
          {selectedImage && (
            <span className="text-[10px] text-zinc-500" data-testid="selected-twin-label">
              Selected: <span className="font-semibold text-zinc-700">{selectedImage.original_name || "Untitled"}</span>
            </span>
          )}
        </div>
        {images.length === 0 ? (
          <p className="text-sm text-zinc-400">No images yet. Upload photos in the AI Twin Gallery first.</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="autocreate-twin-strip">
            {images.map((img) => (
              <button
                key={img.id}
                onClick={() => setSelectedImage(img)}
                className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-[3/4] w-28 sm:w-32 flex-shrink-0 ${
                  selectedImage?.id === img.id
                    ? "border-[#E91E8C] ring-2 ring-[#E91E8C]/30 scale-[1.02]"
                    : "border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid={`select-image-${img.id}`}
                title={img.original_name || ""}
              >
                <img src={`${BACKEND_URL}/api/ai-twin/images/${img.filename}`} alt={img.original_name} className="w-full h-full object-cover" />
                {selectedImage?.id === img.id && (
                  <div className="absolute top-2 right-2 bg-[#E91E8C] rounded-full p-1">
                    <Check size={12} className="text-white" weight="bold" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Setup */}
        <div className="space-y-5">
          {/* Step 1.5: Change Outfit (Optional) */}
          <div className="card p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Change Outfit (Optional)</p>
            <p className="text-xs text-zinc-400">Upload an image of the outfit you want your AI Twin to wear</p>
            <div className="flex gap-3">
              <label
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                  outfitPreview ? "border-green-300 bg-green-50" : "border-zinc-200 hover:border-[#E91E8C] hover:bg-[#E91E8C]/5"
                }`}
                data-testid="autocreate-outfit-upload-btn"
              >
                <input type="file" accept="image/*" onChange={handleOutfitDrop} className="hidden" id="autocreate-outfit-upload" />
                {outfitPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={outfitPreview} alt="Outfit" className="w-12 h-12 object-cover rounded-lg border border-green-300" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-green-700">Outfit selected</p>
                      <p className="text-xs text-green-600">Click to change</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload size={18} className="text-zinc-400" />
                    <span className="text-sm text-zinc-600 font-medium">Upload Outfit Image</span>
                  </>
                )}
              </label>
              {outfitPreview && (
                <button
                  onClick={() => { setOutfitFile(null); setOutfitPreview(null); setOutfitChangedUrl(null); }}
                  className="px-3 py-2 text-xs text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-all"
                  data-testid="autocreate-outfit-remove"
                >
                  Remove
                </button>
              )}
            </div>
            {outfitChangedUrl && selectedImage && (
              <div className="flex items-center gap-3 p-2 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2">
                  <img src={`${BACKEND_URL}/api/ai-twin/images/${selectedImage.filename}`} alt="Before" className="w-12 h-12 object-cover rounded-lg border border-zinc-200" />
                  <ArrowRight size={14} className="text-green-500" />
                  <img src={outfitChangedUrl} alt="After" className="w-12 h-12 object-cover rounded-lg border-2 border-green-400" />
                </div>
                <p className="text-xs text-green-700 font-medium flex-1">Full outfit applied!</p>
                <button onClick={() => { setOutfitChangedUrl(null); setOutfitFile(null); setOutfitPreview(null); }} className="text-xs text-zinc-400 underline">Remove</button>
              </div>
            )}
            {changingOutfit && (
              <div className="flex items-center gap-2 text-xs text-[#E91E8C]">
                <CircleNotch size={14} className="animate-spin" />
                <span>Applying full outfit (2 passes)...</span>
              </div>
            )}
          </div>

          {/* Step 2: Content Settings */}
          <div className="card p-4 space-y-4">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 block">2. Content Settings</label>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs text-zinc-500 font-medium block">Topic</label>
                <button
                  type="button"
                  onClick={() => fetchTopics(true)}
                  disabled={topicsRefreshing || topicsLoading}
                  className="text-[10px] font-semibold uppercase tracking-wider text-[#E91E8C] hover:text-[#c91a7b] disabled:opacity-40 disabled:cursor-wait flex items-center gap-1 transition-colors"
                  data-testid="refresh-topics-btn"
                  title="Generate fresh topic suggestions"
                >
                  <CircleNotch size={11} weight="bold" className={topicsRefreshing ? "animate-spin" : ""} />
                  {topicsRefreshing ? "Refreshing…" : "Refresh"}
                </button>
              </div>
              <select
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                className="input-field text-sm"
                data-testid="auto-topic-select"
              >
                <option value="">Choose a topic...</option>
                {(() => {
                  const list = topicObjects.length > 0
                    ? topicObjects
                    : (personalizedTopics.length > 0 ? personalizedTopics : FALLBACK_TOPICS).map((t) => ({ topic: t, used: false }));
                  return list.map((obj) => (
                    <option key={obj.topic} value={obj.topic}>
                      {obj.used ? "✓ " : ""}{obj.topic}{obj.used ? "  — already used" : ""}
                    </option>
                  ));
                })()}
                <option value="custom">Custom topic...</option>
              </select>
              {topicsLoading && <p className="text-[10px] text-[#E91E8C] mt-1">Loading personalized topics...</p>}
              {topic && topicObjects.some((t) => t.topic === topic && t.used) && (
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                  <span>⚠</span> You&apos;ve already used this topic — consider picking a fresh one.
                </p>
              )}
              {topic === "custom" && (
                <input
                  type="text"
                  placeholder="Type your topic..."
                  className="input-field text-sm mt-2"
                  onChange={(e) => setTopic(e.target.value)}
                  data-testid="auto-topic-custom"
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 font-medium block mb-1">Platform</label>
                <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="input-field text-sm" data-testid="auto-platform">
                  <option value="tiktok">TikTok</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 font-medium block mb-1">Length</label>
                <select value={length} onChange={(e) => setLength(e.target.value)} className="input-field text-sm" data-testid="auto-length">
                  <option value="15sec">15 sec</option>
                  <option value="30sec">30 sec</option>
                  <option value="45-60sec">45-60 sec</option>
                  <option value="1-2min">1-2 min</option>
                  <option value="2-3min">2-3 min</option>
                </select>
                <p className="text-[10px] text-zinc-400 mt-1">
                  Need more than 3 min? Use <strong className="text-[#E91E8C]">Long-Form Studio</strong> — Auto Create caps at 3 minutes per render.
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs text-zinc-500 font-medium block mb-1">Tone</label>
              <select value={tone} onChange={(e) => setTone(e.target.value)} className="input-field text-sm" data-testid="auto-tone">
                <option value="gossip-wisdom">Gossip Wisdom</option>
                <option value="reflective">Reflective</option>
                <option value="hard-truth">Hard Truth</option>
                <option value="storytelling">Storytelling</option>
                <option value="teaching">Teaching</option>
              </select>
            </div>
          </div>

          {/* Step 3: Own Script (Optional) */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">3. Use Your Own Script</label>
              <button
                onClick={() => setUseOwnScript(!useOwnScript)}
                className={`relative w-10 h-5 rounded-full transition-colors ${useOwnScript ? "bg-[#E91E8C]" : "bg-zinc-200"}`}
                data-testid="toggle-own-script"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${useOwnScript ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {useOwnScript && (
              <>
                <textarea
                  value={customScript}
                  onChange={(e) => setCustomScript(e.target.value)}
                  placeholder="Paste or write your own script here... AI will still generate voice, video, and lip-sync from it."
                  className="textarea-field text-sm min-h-[120px]"
                  data-testid="custom-script-input"
                />
                {(() => {
                  const words = customScript.trim().split(/\s+/).filter(Boolean).length;
                  if (words === 0) return null;
                  // ~150 words/min speaking pace
                  const estMinutes = words / 150;
                  const estSeconds = Math.round(estMinutes * 60);
                  const tooLong = estSeconds > 180;
                  return (
                    <p className={`text-[11px] ${tooLong ? "text-amber-600 font-semibold" : "text-zinc-400"}`} data-testid="custom-script-length-meter">
                      ~{words} words · est. {estMinutes < 1 ? `${estSeconds}s` : `${estMinutes.toFixed(1)} min`} spoken
                      {tooLong && <> · ⚠️ over 3 min — will be trimmed to 3 min. Shorten, or use Long-Form Studio.</>}
                    </p>
                  );
                })()}
              </>
            )}
            {!useOwnScript && (
              <p className="text-xs text-zinc-400">Toggle on to skip AI script generation and use your own words.</p>
            )}
          </div>

          {/* Fast Mode Toggle */}
          <div className="card p-4 space-y-2 border-amber-200 bg-amber-50/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightning size={18} weight="fill" className="text-amber-500" />
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Fast Mode</label>
              </div>
              <button
                onClick={() => setSkipLipsync(!skipLipsync)}
                className={`relative w-10 h-5 rounded-full transition-colors ${skipLipsync ? "bg-amber-500" : "bg-zinc-200"}`}
                data-testid="toggle-fast-mode"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${skipLipsync ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              {skipLipsync
                ? "Lip-sync OFF — video finishes ~3-5 min faster. Audio overlays directly onto the video."
                : "Lip-sync ON — AI matches mouth movement to voice (adds 3-5 min). Toggle on for faster output."}
            </p>
          </div>

          {/* Talking Photo Style / Lip Sync picker */}
          {!skipLipsync && (
            <div className="card p-4 space-y-4 border-[#E91E8C]/20 bg-gradient-to-br from-[#E91E8C]/5 to-transparent" data-testid="lipsync-source-picker">
              <div>
                <div className="flex items-center gap-2">
                  <Sparkle size={18} weight="fill" className="text-[#E91E8C]" />
                  <label className="text-xs font-bold uppercase tracking-wider text-zinc-700">Video Source</label>
                </div>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Pick a base video (faster, more natural motion) or use Talking Photo (photo only, slower).
                </p>
              </div>

              {/* Mode selector */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const dflt = baseVideos.find((v) => v.is_default && v.status === "ready");
                    setSelectedBaseVideoId(dflt?.id || baseVideos.find(v => v.status === "ready")?.id || "");
                    if (talkingPhotoProvider === "pruna" || talkingPhotoProvider === "fabric") setTalkingPhotoProvider("auto");
                  }}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${selectedBaseVideoId ? "border-[#E91E8C] bg-[#E91E8C]/10" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="pick-lipsync"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <VideoCamera size={13} weight="fill" className="text-[#E91E8C]" />
                    <p className="text-xs font-bold text-zinc-900">Lip Sync</p>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">FASTER · MORE NATURAL</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 leading-tight">VEED Lipsync (fal.ai) — needs a base video. Real motion, blinks, head tilts.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedBaseVideoId(null)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${selectedBaseVideoId === null ? "border-[#E91E8C] bg-[#E91E8C]/10" : "border-zinc-200 hover:border-zinc-300"}`}
                  data-testid="pick-talking-photo"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <p className="text-xs font-bold text-zinc-900">Talking Photo</p>
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">PHOTO ONLY</span>
                  </div>
                  <p className="text-[10px] text-zinc-500 leading-tight">VEED Fabric 1.0 (fast · ~1–2 min) — animates your AI Twin photo from scratch.</p>
                </button>
              </div>

              {/* Sub-options when Lip Sync selected */}
              {selectedBaseVideoId !== null && (
                <div className="space-y-3 pt-2 border-t border-zinc-100">
                  {/* Render engine for lip sync: VEED (default) vs HeyGen HD */}
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1.5">Render engine</label>
                    <div className="grid grid-cols-2 gap-1.5">
                      <button
                        type="button"
                        onClick={() => setTalkingPhotoProvider("auto")}
                        className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider !== "heygen" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                        data-testid="lipsync-engine-veed"
                        title="VEED Lipsync (fal.ai) — fast & cheap"
                      >
                        <p className="text-[11px] font-bold text-zinc-900 leading-tight">🎤 VEED Lipsync</p>
                        <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Fast & cheap · standard</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setTalkingPhotoProvider("heygen")}
                        className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "heygen" ? "border-violet-500 bg-violet-50" : "border-zinc-200 hover:border-zinc-300"}`}
                        data-testid="lipsync-engine-heygen"
                        title="HeyGen HD lip-sync — premium quality. Uses extra video credits (scales with length)."
                      >
                        <div className="flex items-center gap-1">
                          <p className="text-[11px] font-bold text-zinc-900 leading-tight">💎 HeyGen</p>
                          <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-violet-200 text-violet-800">HD</span>
                        </div>
                        <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Premium · uses credits</p>
                      </button>
                    </div>
                  </div>
                  {/* Library picker */}
                  {baseVideos.length > 0 && (
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1.5">From your Base Video Library</label>
                      <div className="flex flex-wrap gap-2">
                        {baseVideos.filter(v => v.status === "ready" || !v.status).map((v) => (
                          <button
                            key={v.id}
                            type="button"
                            onClick={() => setSelectedBaseVideoId(v.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${selectedBaseVideoId === v.id ? "border-[#E91E8C] bg-[#E91E8C]/10 text-[#E91E8C]" : "border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}
                            data-testid={`pick-base-video-${v.id}`}
                          >
                            {v.is_default && <Star size={10} weight="fill" />}
                            <span className="max-w-[140px] truncate">{v.name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Auto-generate idle clip */}
                  {selectedImage && (
                    <div className="flex items-center gap-2 p-2 rounded-lg bg-white border border-zinc-200">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-zinc-900">No clip? Auto-generate one</p>
                        <p className="text-[10px] text-zinc-500">Seedance makes a 10s idle motion clip from your AI Twin (~2 min, cached per image).</p>
                      </div>
                      <button
                        type="button"
                        onClick={handleGenerateIdle}
                        disabled={generatingIdle}
                        className="flex-shrink-0 px-3 py-1.5 bg-zinc-900 text-white text-xs font-semibold rounded-lg hover:bg-zinc-700 disabled:opacity-50 flex items-center gap-1"
                        data-testid="generate-idle-btn"
                      >
                        {generatingIdle ? <><CircleNotch size={11} className="animate-spin" /> Generating...</> : <>Generate</>}
                      </button>
                    </div>
                  )}

                  {/* Reuse lifestyle videos */}
                  {lifestyleJobs.length > 0 && (
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1.5">
                        Or reuse a past Lifestyle Video ({lifestyleJobs.length} available)
                      </label>
                      <select
                        onChange={(e) => { if (e.target.value) { handleReuseLifestyle(e.target.value); e.target.value = ""; } }}
                        defaultValue=""
                        className="w-full px-3 py-2 rounded-lg text-xs border border-zinc-200 bg-white text-zinc-700 hover:border-[#E91E8C]/40 focus:border-[#E91E8C] focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/20 transition-all"
                        data-testid="reuse-lifestyle-dropdown"
                      >
                        <option value="" disabled>— Pick a Lifestyle video to reuse —</option>
                        {lifestyleJobs.map((j) => {
                          const d = j.created_at ? new Date(j.created_at) : null;
                          const dateStr = d ? `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "";
                          const label = `${j.vibe || "Lifestyle"}${j.style ? " · " + j.style : ""}`;
                          return (
                            <option key={j.id} value={j.id}>
                              {dateStr ? `${dateStr} · ` : ""}{label}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}

                  {/* Reuse past Auto Create videos (already face-preserved, perfect as bases) */}
                  {(() => {
                    const reusableAutoCreate = (jobs || []).filter((j) => j.status === "completed" && j.output_url);
                    if (reusableAutoCreate.length === 0) return null;
                    return (
                      <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block mb-1.5">
                          Or reuse a past Auto Create video ({reusableAutoCreate.length} available)
                        </label>
                        <select
                          onChange={(e) => { if (e.target.value) { handleReuseAutoCreate(e.target.value); e.target.value = ""; } }}
                          defaultValue=""
                          className="w-full px-3 py-2 rounded-lg text-xs border border-zinc-200 bg-white text-zinc-700 hover:border-[#E91E8C]/40 focus:border-[#E91E8C] focus:outline-none focus:ring-2 focus:ring-[#E91E8C]/20 transition-all"
                          data-testid="reuse-autocreate-dropdown"
                        >
                          <option value="" disabled>— Pick an Auto Create video to reuse —</option>
                          {reusableAutoCreate.map((j) => {
                            const d = j.created_at ? new Date(j.created_at) : null;
                            const dateStr = d ? `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}` : "";
                            const title = (j.topic || "Auto Create clip").slice(0, 60);
                            return (
                              <option key={j.id} value={j.id}>
                                {dateStr ? `${dateStr} · ` : ""}{title}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    );
                  })()}

                  {baseVideos.length === 0 && lifestyleJobs.length === 0 && (jobs || []).filter((j) => j.status === "completed" && j.output_url).length === 0 && (
                    <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      💡 No base videos yet. Upload one in <strong>Account Settings → Base Videos</strong>, or pick Talking Photo above.
                    </p>
                  )}
                </div>
              )}

              {/* Talking Photo provider (only when Talking Photo selected) */}
              {selectedBaseVideoId === null && (
                <div className="pt-2 border-t border-zinc-100 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Video Model</label>
                    {talkingPhotoProvider === "pruna" && (
                      <select
                        value={talkingPhotoResolution === "1080p" ? "1080p" : "720p"}
                        onChange={(e) => setTalkingPhotoResolution(e.target.value)}
                        className="text-[10px] border border-emerald-200 rounded px-1.5 py-0.5"
                        data-testid="pruna-resolution"
                      >
                        <option value="720p">720p · $0.025/sec</option>
                        <option value="1080p">1080p · $0.045/sec</option>
                      </select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                    <button
                      type="button"
                      onClick={() => setTalkingPhotoProvider("auto")}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "auto" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="provider-auto"
                      title="Recommended — Pruna for talking-photo, VEED for lip-sync. Best value."
                    >
                      <p className="text-[11px] font-bold text-zinc-900 leading-tight">Auto ✨</p>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Best-value engine</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTalkingPhotoProvider("pruna"); if (talkingPhotoResolution === "480p") setTalkingPhotoResolution("720p"); }}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "pruna" ? "border-emerald-500 bg-emerald-50" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="provider-pruna"
                      title="Replicate Pruna P-Video-Avatar — 4× cheaper than Fabric at higher res. New — test-drive on real content first."
                    >
                      <div className="flex items-center gap-1">
                        <p className="text-[11px] font-bold text-zinc-900 leading-tight">🌱 Pruna</p>
                        <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-emerald-200 text-emerald-800">4× CHEAPER</span>
                      </div>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">720p · $0.025/sec</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTalkingPhotoProvider("heygen")}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "heygen" ? "border-violet-500 bg-violet-50" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="provider-heygen"
                      title="HeyGen HD (Avatar IV) — highest-quality talking avatar. Premium: uses extra video credits (scales with length)."
                    >
                      <div className="flex items-center gap-1">
                        <p className="text-[11px] font-bold text-zinc-900 leading-tight">💎 HeyGen</p>
                        <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-violet-200 text-violet-800">HD</span>
                      </div>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">Best quality · 1080p</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setTalkingPhotoProvider("veed")}
                      className={`p-2 rounded-lg border text-left transition-all ${talkingPhotoProvider === "veed" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                      data-testid="provider-veed"
                      title="VEED Lipsync — cheapest & fastest for lip-sync mode"
                    >
                      <p className="text-[11px] font-bold text-zinc-900 leading-tight">🎤 VEED Lipsync</p>
                      <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">~1 min · lip-sync only</p>
                    </button>
                  </div>

                  {/* Cost estimate + cheaper-mode nudge (iter72 · iter73 Pruna · HeyGen HD) */}
                  {(() => {
                    const secondsMap = { "15sec": 15, "30sec": 30, "45-60sec": 55, "1-2min": 105, "2-3min": 165 };
                    const secs = secondsMap[length] || 30;

                    // HeyGen HD is metered in video credits (ceil(secs/15), floor 2),
                    // not a per-second $ rate the user pays directly.
                    if (talkingPhotoProvider === "heygen") {
                      const credits = Math.max(2, Math.ceil(secs / 15));
                      return (
                        <div className="mt-2 p-2.5 rounded-lg space-y-1.5 bg-violet-50 border border-violet-200" data-testid="talking-photo-cost-nudge">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-violet-800">💎 HeyGen HD — premium</span>
                            <span className="text-sm font-black text-violet-900" data-testid="talking-photo-est-cost">{credits} credits</span>
                          </div>
                          <p className="text-[10px] leading-tight text-violet-800">
                            Highest-quality avatar (1080p). Uses <strong>{credits} of your monthly video credits</strong> for this {secs}s render (scales with length). Longer HD videos use more credits.
                          </p>
                          {heygenWallet && heygenWallet.low && (
                            <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-amber-100 border border-amber-300" data-testid="heygen-wallet-warning">
                              <span className="text-[11px]">⚠️</span>
                              <p className="text-[10px] leading-tight text-amber-900">
                                {heygenWallet.configured === false ? (
                                  <>HeyGen isn't configured on the server yet — add <strong>HEYGEN_API_KEY</strong> in Secrets.</>
                                ) : heygenWallet.balance != null ? (
                                  <>HeyGen wallet is low (<strong>${heygenWallet.balance}</strong>). Top up at <a href="https://app.heygen.com" target="_blank" rel="noreferrer" className="underline font-semibold">app.heygen.com</a> or your HD render may fail mid-way.</>
                                ) : (
                                  <>Couldn't confirm your HeyGen balance — if the render fails, top up at <a href="https://app.heygen.com" target="_blank" rel="noreferrer" className="underline font-semibold">app.heygen.com</a>.</>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Effective per-second rate for the fal/replicate engines.
                    // "auto" now routes to Pruna (Fabric retired — too expensive).
                    let rate = 0.025;
                    let providerLabel = "Pruna 720p (auto)";
                    if (talkingPhotoProvider === "pruna") {
                      rate = talkingPhotoResolution === "1080p" ? 0.045 : 0.025;
                      providerLabel = `Pruna ${talkingPhotoResolution === "1080p" ? "1080p" : "720p"}`;
                    } else if (talkingPhotoProvider === "veed") {
                      rate = 0.007;
                      providerLabel = "VEED Lipsync";
                    }
                    const estCost = (secs * rate).toFixed(2);
                    const cheapCost = (secs * 0.007).toFixed(2);
                    const savings = ((secs * rate) - (secs * 0.007)).toFixed(2);
                    const isCheap = talkingPhotoProvider === "veed" || (talkingPhotoProvider === "pruna" && parseFloat(estCost) < 1.5);
                    return (
                      <div className={`mt-2 p-2.5 rounded-lg space-y-1.5 ${isCheap ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"}`} data-testid="talking-photo-cost-nudge">
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${isCheap ? "text-emerald-800" : "text-amber-800"}`}>Est. cost this render</span>
                          <span className={`text-sm font-black ${isCheap ? "text-emerald-900" : "text-amber-900"}`} data-testid="talking-photo-est-cost">${estCost}</span>
                        </div>
                        <p className={`text-[10px] leading-tight ${isCheap ? "text-emerald-800" : "text-amber-800"}`}>
                          {providerLabel} · ${rate.toFixed(3)}/sec × {secs}s = ~${estCost}.
                        </p>
                        {!isCheap && (
                          <p className="text-[10px] text-emerald-700 leading-tight">
                            💡 <strong>Save ~${savings}</strong> — try <strong>🌱 Pruna 720p</strong> (~${(secs * 0.025).toFixed(2)}) or <strong>Lip Sync</strong> with a Base Video (~${cheapCost}).
                            {selectedImage && baseVideos.length === 0 && (
                              <span> Tap <strong>&ldquo;Auto-generate idle clip&rdquo;</strong> above to unlock Lip Sync.</span>
                            )}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Talking Photo sub-mode — legacy Fabric/MH only; retired engines removed */}
              {selectedBaseVideoId === null && talkingPhotoProvider !== "auto" && talkingPhotoProvider !== "fabric" && talkingPhotoProvider !== "pruna" && talkingPhotoProvider !== "heygen" && (
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={() => setTalkingPhotoMode("prompted")}
                    className={`p-2.5 rounded-lg border text-left transition-all ${talkingPhotoMode === "prompted" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                    data-testid="mode-prompted"
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Lightning size={11} weight="fill" className="text-amber-500" />
                      <p className="text-xs font-bold text-zinc-900">Prompted</p>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">2–3× FASTER</span>
                    </div>
                    <p className="text-[10px] text-zinc-500">Natural lip sync. Slightly lower likeness.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setTalkingPhotoMode("realistic")}
                    className={`p-2.5 rounded-lg border text-left transition-all ${talkingPhotoMode === "realistic" ? "border-[#E91E8C] bg-[#E91E8C]/5" : "border-zinc-200 hover:border-zinc-300"}`}
                    data-testid="mode-realistic"
                  >
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-xs font-bold text-zinc-900">Realistic</p>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">MAX LIKENESS</span>
                    </div>
                    <p className="text-[10px] text-zinc-500">Powered by VEED Fabric 1.0 — fast (~1–2 min for 60s), high quality.</p>
                  </button>
                </div>
              )}
            </div>
          )}


          {/* Step 4: AI B-Roll (Optional) */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">4. AI B-Roll</label>
              <button
                onClick={() => setIncludeBroll(!includeBroll)}
                className={`relative w-10 h-5 rounded-full transition-colors ${includeBroll ? "bg-[#E91E8C]" : "bg-zinc-200"}`}
                data-testid="toggle-broll"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${includeBroll ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {includeBroll ? (
              <div className="space-y-3">
                <p className="text-xs text-zinc-400">AI will analyze your script, pick 2-3 key moments, and generate cinematic cutaway clips that splice into your video automatically.</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-zinc-50 rounded-lg p-2.5 text-center">
                    <FilmStrip size={20} weight="duotone" className="text-[#E91E8C] mx-auto mb-1" />
                    <p className="text-[10px] font-semibold text-zinc-700">Script Analysis</p>
                    <p className="text-[9px] text-zinc-400">AI picks key moments</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-2.5 text-center">
                    <VideoCamera size={20} weight="duotone" className="text-[#E91E8C] mx-auto mb-1" />
                    <p className="text-[10px] font-semibold text-zinc-700">Generate Clips</p>
                    <p className="text-[9px] text-zinc-400">AI creates visuals</p>
                  </div>
                  <div className="bg-zinc-50 rounded-lg p-2.5 text-center">
                    <Sparkle size={20} weight="duotone" className="text-[#E91E8C] mx-auto mb-1" />
                    <p className="text-[10px] font-semibold text-zinc-700">Auto Splice</p>
                    <p className="text-[9px] text-zinc-400">Inserted at right spots</p>
                  </div>
                </div>
                <div className="bg-[#E91E8C]/5 border border-[#E91E8C]/20 rounded-lg p-2.5">
                  <p className="text-xs text-zinc-500">Adds ~4-6 min to processing. Generates 2-3 five-second cutaway clips via AI text-to-video.</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-400">Toggle on to auto-generate contextual B-Roll cutaways from your script.</p>
            )}
          </div>

          {/* Step 4b: Visual Polish (Visual Hook + Dynamic Framing) */}
          <div className="card p-4 space-y-3" data-testid="visual-polish-card">
            <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">4b. Visual Polish <span className="text-zinc-300 font-normal normal-case ml-1">($0 extra · no render-time cost)</span></label>

            <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-800">Visual Hook Overlay</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">Burns bold kinetic text of your hook over the first 2.5s to stop the scroll.</p>
              </div>
              <button
                type="button"
                onClick={() => setVisualHook(!visualHook)}
                className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${visualHook ? "bg-[#E91E8C]" : "bg-zinc-200"}`}
                data-testid="toggle-visual-hook"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${visualHook ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-800">Dynamic Framing</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">Cuts between wide · medium · close-up every 3–4s — fakes multi-shot coverage from one take. Best with videos ≥ 15s.</p>
              </div>
              <button
                type="button"
                onClick={() => setDynamicFraming(!dynamicFraming)}
                className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${dynamicFraming ? "bg-[#E91E8C]" : "bg-zinc-200"}`}
                data-testid="toggle-dynamic-framing"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${dynamicFraming ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>

          {/* Step 5: AI Captions (Optional) */}
          <div className="card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">5. AI Captions</label>
              <button
                onClick={() => setIncludeCaptions(!includeCaptions)}
                className={`relative w-10 h-5 rounded-full transition-colors ${includeCaptions ? "bg-[#E91E8C]" : "bg-zinc-200"}`}
                data-testid="toggle-captions"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${includeCaptions ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            {includeCaptions ? (
              <div className="space-y-3">
                <p className="text-xs text-zinc-400">Animated captions will be burned into your video. Pick a style:</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[340px] overflow-y-auto pr-1" data-testid="caption-theme-grid">
                  {CAPTION_THEMES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setCaptionTheme(t.id)}
                      data-testid={`caption-theme-${t.id}`}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all text-left ${captionTheme === t.id ? "border-[#E91E8C] ring-1 ring-[#E91E8C]/30" : "border-zinc-200 hover:border-zinc-300"}`}
                    >
                      {/* Visual preview */}
                      <div
                        className="h-16 flex items-center justify-center px-2"
                        style={{ background: t.bg }}
                      >
                        <span style={{
                          fontWeight: t.font,
                          fontFamily: t.fontFamily,
                          color: t.color,
                          fontSize: "13px",
                          textTransform: t.transform,
                          fontStyle: t.fontStyle || "normal",
                          textShadow: t.glow
                            ? `0 0 8px ${t.highlight}, 0 0 16px ${t.highlight}`
                            : t.shadow
                              ? `2px 2px 6px rgba(0,0,0,0.7)`
                              : t.stroke
                                ? `1px 1px 0 ${t.strokeColor || "#000"}, -1px -1px 0 ${t.strokeColor || "#000"}, 1px -1px 0 ${t.strokeColor || "#000"}, -1px 1px 0 ${t.strokeColor || "#000"}`
                                : "none",
                          letterSpacing: t.transform === "uppercase" ? "0.05em" : "normal",
                          lineHeight: 1.2,
                        }}>
                          the secret{" "}
                          <span style={{
                            color: t.highlight,
                            fontWeight: "900",
                            textShadow: t.glow
                              ? `0 0 10px ${t.highlight}, 0 0 20px ${t.highlight}`
                              : t.stroke
                                ? `1px 1px 0 ${t.strokeColor || "#000"}, -1px -1px 0 ${t.strokeColor || "#000"}, 1px -1px 0 ${t.strokeColor || "#000"}, -1px 1px 0 ${t.strokeColor || "#000"}`
                                : "none",
                          }}>is</span>
                        </span>
                      </div>
                      {/* Label */}
                      <div className="px-2 py-1.5 bg-white border-t border-zinc-100">
                        <p className="text-[11px] font-bold text-zinc-800 truncate">{t.name}</p>
                        <p className="text-[10px] text-zinc-400 truncate">{t.desc}</p>
                      </div>
                      {/* Selected badge */}
                      {captionTheme === t.id && (
                        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#E91E8C] flex items-center justify-center">
                          <Check size={10} weight="bold" className="text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                {/* Hidden select for form compatibility */}
                <select
                  value={captionTheme}
                  onChange={(e) => setCaptionTheme(e.target.value)}
                  className="sr-only"
                  data-testid="caption-theme-select"
                >
                  {CAPTION_THEMES.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <div className="bg-[#E91E8C]/5 border border-[#E91E8C]/20 rounded-lg p-2.5">
                  <p className="text-xs text-zinc-500">Adds ~2-3 min to processing. Powered by Vadoo AI via muapi.ai</p>
                </div>
              </div>
            ) : (
              <p className="text-xs text-zinc-400">Toggle on to add animated subtitles to your video.</p>
            )}
          </div>

          {/* Step 6: Voice */}
          <div className="card p-4 space-y-3" data-testid="autocreate-voice-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <SpeakerHigh size={18} className="text-[#E91E8C]" />
                <label className="text-xs font-bold uppercase tracking-wider text-zinc-500">6. Voice</label>
              </div>
              <p className="text-[10px] text-zinc-400">
                {voiceProvider === "openai" ? "Free · OpenAI TTS" : "BYOK · ElevenLabs"}
              </p>
            </div>

            {/* Provider toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => { setVoiceProvider("openai"); persistVoicePref("openai", openaiVoice); }}
                data-testid="autocreate-voice-provider-openai"
                className={`p-2 rounded-lg border text-left transition-all ${
                  voiceProvider === "openai"
                    ? "border-[#E91E8C] bg-[#E91E8C]/5"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <p className="text-xs font-bold text-zinc-900">🆓 Stock Voice</p>
                <p className="text-[10px] text-zinc-500">9 free preset voices · no setup</p>
              </button>
              <button
                type="button"
                onClick={() => { setVoiceProvider("elevenlabs"); if (elevenlabsVoice) persistVoicePref("elevenlabs", elevenlabsVoice); }}
                data-testid="autocreate-voice-provider-elevenlabs"
                className={`p-2 rounded-lg border text-left transition-all ${
                  voiceProvider === "elevenlabs"
                    ? "border-[#E91E8C] bg-[#E91E8C]/5"
                    : "border-zinc-200 hover:border-zinc-300"
                }`}
              >
                <p className="text-xs font-bold text-zinc-900">🎤 My Cloned Voice</p>
                <p className="text-[10px] text-zinc-500">
                  {elevenlabsAvailable
                    ? `ElevenLabs · ${elevenlabsVoices.filter((v) => v.category === "cloned").length} cloned voices`
                    : "ElevenLabs · BYOK required"}
                </p>
              </button>
            </div>

            {/* OpenAI voice picker */}
            {voiceProvider === "openai" && (
              <div className="space-y-1.5" data-testid="autocreate-openai-voice-picker">
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Pick a voice — ❤ = favorites first
                </p>
                <div className="grid grid-cols-3 gap-1.5 max-h-72 overflow-y-auto">
                  {[...openaiVoices]
                    .sort((a, b) => {
                      const aFav = voiceFavorites.includes(`openai:${a.id}`) ? 0 : 1;
                      const bFav = voiceFavorites.includes(`openai:${b.id}`) ? 0 : 1;
                      return aFav - bFav;
                    })
                    .map((v) => {
                      const isFav = voiceFavorites.includes(`openai:${v.id}`);
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => { setOpenaiVoice(v.id); persistVoicePref("openai", v.id); }}
                          data-testid={`autocreate-openai-voice-${v.id}`}
                          className={`p-2 rounded-lg border text-left transition-all ${
                            openaiVoice === v.id
                              ? "border-[#E91E8C] bg-[#E91E8C]/5"
                              : "border-zinc-200 hover:border-zinc-300"
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <p className="text-xs font-bold text-zinc-900 truncate">{v.label}</p>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => toggleVoiceFavorite("openai", v.id, e)}
                              onKeyDown={(e) => { if (e.key === "Enter") toggleVoiceFavorite("openai", v.id, e); }}
                              className={`text-sm cursor-pointer leading-none ${isFav ? "" : "opacity-30 hover:opacity-70"}`}
                              data-testid={`autocreate-favorite-openai-${v.id}`}
                              title={isFav ? "Remove from favorites" : "Add to favorites"}
                            >
                              {isFav ? "❤️" : "🤍"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); previewVoice(v.id); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); previewVoice(v.id); } }}
                              className="text-[9px] text-[#E91E8C] hover:text-[#C8186F] font-semibold cursor-pointer"
                              data-testid={`autocreate-preview-${v.id}`}
                            >
                              {previewingVoice === v.id ? "⏵ playing" : "▶ preview"}
                            </span>
                          </div>
                          <p className="text-[9px] text-zinc-500 leading-tight mt-0.5">{v.description}</p>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* ElevenLabs voice picker */}
            {voiceProvider === "elevenlabs" && (
              <div className="space-y-1.5" data-testid="autocreate-elevenlabs-voice-picker">
                {!elevenlabsAvailable ? (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1.5">
                    <p className="font-semibold">ElevenLabs not connected yet</p>
                    <p className="text-amber-800">
                      Sign up free at{" "}
                      <a href="https://elevenlabs.io/app/sign-up" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                        elevenlabs.io
                      </a>{" "}
                      (no card required, 10k chars/mo free) → copy your API key → paste it in{" "}
                      <a href="/account-settings" className="underline font-semibold">Account Settings → API Keys</a>.
                    </p>
                  </div>
                ) : elevenlabsVoices.length === 0 ? (
                  <p className="text-xs text-zinc-500">No voices found on your ElevenLabs account. Clone one at elevenlabs.io/app/voice-lab.</p>
                ) : (
                  <>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Your ElevenLabs voices</p>
                    <select
                      value={elevenlabsVoice}
                      onChange={(e) => { setElevenlabsVoice(e.target.value); persistVoicePref("elevenlabs", e.target.value); }}
                      data-testid="autocreate-elevenlabs-voice-select"
                      className="w-full text-sm border border-zinc-200 rounded px-2 py-1.5"
                    >
                      <option value="">— pick a voice —</option>
                      {elevenlabsVoices.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.category === "cloned" ? "(your clone)" : v.category === "professional" ? "(pro)" : ""}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Plan usage pill — shows remaining videos + cap per video.
              Hidden for admin/unlimited users. Links to /account settings
              on click for upgrade. */}
          {planInfo && !planInfo.is_admin_unlimited && (
            <div className="card p-3 flex items-center justify-between gap-2" data-testid="plan-usage-pill">
              <div className="flex items-center gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-bold ${
                  planInfo.tier === "free" ? "bg-zinc-100 text-zinc-700" : "bg-[#E91E8C]/10 text-[#E91E8C]"
                }`}>
                  {planInfo.plan.name}
                </span>
                <span className="text-zinc-600">
                  <span className={`font-bold ${
                    (planInfo.usage.videos_limit - planInfo.usage.videos_used) <= 1 ? "text-red-500" : "text-zinc-900"
                  }`}>
                    {Math.max(0, planInfo.usage.videos_limit - planInfo.usage.videos_used)}
                  </span> of {planInfo.usage.videos_limit} videos left · max {planInfo.plan.max_seconds_per_video}s each
                </span>
              </div>
              <button
                type="button"
                onClick={() => document.querySelector('[data-testid="nav-account"]')?.click()}
                className="text-[10px] font-bold text-[#E91E8C] hover:text-[#C8186F] underline"
                data-testid="plan-upgrade-link"
              >
                {planInfo.tier === "free" ? "Upgrade →" : "Manage plan →"}
              </button>
            </div>
          )}

          {/* Review & Edit Script panel (opt-in — appears after "Generate & Review Script") */}
          {reviewMode && (
            <div className="card p-4 space-y-3 border-[#E91E8C]/30 bg-gradient-to-br from-[#E91E8C]/5 to-transparent" data-testid="script-review-panel">
              <div className="flex items-center gap-2">
                <Sparkle size={16} weight="fill" className="text-[#E91E8C]" />
                <h3 className="text-sm font-bold text-zinc-800">Review & edit your script</h3>
              </div>
              <p className="text-[11px] text-zinc-500 -mt-1">Tweak anything before rendering — no credits are spent until you hit Render.</p>
              <textarea
                value={reviewScript}
                onChange={(e) => setReviewScript(e.target.value)}
                rows={10}
                className="input-field text-sm w-full font-mono leading-relaxed"
                data-testid="review-script-textarea"
              />
              {reviewCaption && (
                <div className="text-[11px] text-zinc-500 bg-zinc-50 rounded-lg p-2.5 border border-zinc-100" data-testid="review-caption-preview">
                  <span className="font-semibold text-zinc-600">Caption:</span> {reviewCaption} {reviewHashtags && <span className="text-[#E91E8C]">{reviewHashtags}</span>}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => startAutoCreate(reviewScript)}
                  disabled={creating || !selectedImage || !reviewScript.trim() || (voiceProvider === "elevenlabs" && !elevenlabsVoice)}
                  className="btn-primary flex-1 text-sm py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="render-reviewed-script-btn"
                >
                  {creating ? <><CircleNotch size={18} className="animate-spin" /> Rendering...</> : <><Check size={18} weight="bold" /> Looks good — Render Video</>}
                </button>
                <button
                  onClick={previewScriptForReview}
                  disabled={previewing || creating}
                  className="px-4 py-3 rounded-xl text-sm font-semibold border border-zinc-200 text-zinc-600 hover:bg-zinc-50 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="regenerate-script-btn"
                >
                  {previewing ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} />} Regenerate
                </button>
                <button
                  onClick={() => setReviewMode(false)}
                  disabled={creating}
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-zinc-500 hover:bg-zinc-50 disabled:opacity-50"
                  data-testid="cancel-review-btn"
                >
                  Cancel
                </button>
              </div>
              {!selectedImage && (
                <p className="text-[11px] text-amber-600 font-medium" data-testid="review-no-image-warning">Pick an AI Twin above before rendering.</p>
              )}
            </div>
          )}

          {/* Create Buttons */}
          {!reviewMode && (
            <div className="space-y-2">
              <button
                onClick={() => startAutoCreate()}
                disabled={creating || !selectedImage || (!useOwnScript && (!topic || topic === "custom")) || (useOwnScript && !customScript.trim()) || (voiceProvider === "elevenlabs" && !elevenlabsVoice)}
                className="btn-primary w-full text-sm py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="auto-create-btn"
              >
                {creating ? (
                  <>
                    <CircleNotch size={18} className="animate-spin" />
                    Creating Content...
                  </>
                ) : (
                  <>
                    <Sparkle size={18} weight="fill" />
                    Auto Create Content
                  </>
                )}
              </button>
              {!useOwnScript && (
                <button
                  onClick={previewScriptForReview}
                  disabled={previewing || creating || !topic || topic === "custom"}
                  className="w-full text-sm py-2.5 rounded-xl font-semibold border border-[#E91E8C]/40 text-[#E91E8C] hover:bg-[#E91E8C]/5 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="generate-review-script-btn"
                >
                  {previewing ? <><CircleNotch size={16} className="animate-spin" /> Generating script...</> : <><Check size={16} weight="bold" /> Generate &amp; Review Script First</>}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right: Progress & Result */}
        <div className="space-y-5">
          {/* Progress Stepper */}
          {(creating || currentStep === "done") && (
            <div className="card p-5" data-testid="progress-stepper">
              {(() => {
                const activeStep = STEPS.find((s) => s.key === currentStep) || STEPS[0];
                const pct = activeStep.pct;
                const isDone = currentStep === "done";
                const ActiveIcon = activeStep.icon;
                return (
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDone ? "bg-green-500" : "bg-[#E91E8C]"} text-white`}>
                          {isDone ? <Check size={14} weight="bold" /> : <CircleNotch size={14} className="animate-spin" />}
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${isDone ? "text-green-700" : "text-zinc-900"}`}>{activeStep.label}</p>
                          <p className="text-xs text-zinc-400">Step {STEPS.findIndex((s) => s.key === currentStep) + 1} of {STEPS.length}</p>
                        </div>
                      </div>
                      <span className={`text-lg font-bold tabular-nums ${isDone ? "text-green-600" : "text-[#E91E8C]"}`} data-testid="progress-percentage">{pct}%</span>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full h-3 bg-zinc-100 rounded-full overflow-hidden" data-testid="progress-bar">
                      <div
                        className={`h-full rounded-full transition-all duration-1000 ease-out ${isDone ? "bg-green-500" : "bg-gradient-to-r from-[#E91E8C] to-[#ff6ec7]"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    {/* Step dots */}
                    <div className="flex items-center justify-between">
                      {STEPS.map((step, i) => {
                        const stepIndex = STEPS.findIndex((s) => s.key === currentStep);
                        const isComplete = i < stepIndex || isDone;
                        const isActive = step.key === currentStep && !isDone;
                        const StepIcon = step.icon;
                        return (
                          <div key={step.key} className="flex flex-col items-center gap-1" title={step.label}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                              isComplete ? "bg-green-500 text-white" :
                              isActive ? "bg-[#E91E8C] text-white ring-4 ring-[#E91E8C]/20" :
                              "bg-zinc-100 text-zinc-300"
                            }`}>
                              {isComplete ? <Check size={10} weight="bold" /> : <StepIcon size={10} />}
                            </div>
                            <span className={`text-[10px] leading-tight text-center ${
                              isComplete ? "text-green-600 font-medium" :
                              isActive ? "text-[#E91E8C] font-semibold" :
                              "text-zinc-300"
                            }`}>{step.label.split(" ").pop()}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Error — supports multi-line friendly messages (whitespace-pre-line
              preserves the newlines our backend uses in "top up / switch model" hints). */}
          {error && (
            <div className="card p-4 bg-red-50 border-red-200" data-testid="auto-create-error">
              <p className="text-sm text-red-700 font-semibold">Video generation failed</p>
              <p className="text-xs text-red-600 mt-1.5 whitespace-pre-line leading-relaxed">{error}</p>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="card p-4 space-y-4" data-testid="auto-create-result">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Script</p>
                <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-700 max-h-48 overflow-y-auto whitespace-pre-wrap">
                  {result.script}
                </div>
              </div>

              {(result.caption || result.hashtags) && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Caption + Hashtags</p>
                  <div className="bg-[#E91E8C]/5 border border-[#E91E8C]/20 rounded-lg p-3 text-sm text-zinc-700 space-y-2">
                    {result.caption && <p className="whitespace-pre-wrap">{result.caption}</p>}
                    {result.hashtags && <p className="text-[#E91E8C] font-medium text-xs">{result.hashtags}</p>}
                  </div>
                </div>
              )}

              {result.audio_url && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Cloned Voice</p>
                  <audio controls className="w-full" src={`${BACKEND_URL}${result.audio_url}`} />
                </div>
              )}

              {result.output_url ? (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Final Video</p>
                    {result.video_provider && (() => {
                      const vp = result.video_provider;
                      const map = {
                        fal_fabric: { cls: "bg-emerald-100 text-emerald-700", label: "⚡ VEED Fabric", title: "Rendered with VEED Fabric 1.0 (fast)" },
                        fal_veed_lipsync: { cls: "bg-emerald-100 text-emerald-700", label: "🎤 VEED Lipsync", title: "Rendered with VEED Lipsync (fal.ai)" },
                        replicate_pruna: { cls: "bg-emerald-100 text-emerald-700", label: "🌱 Pruna", title: "Rendered with Replicate Pruna" },
                        heygen: { cls: "bg-violet-100 text-violet-700", label: "💎 HeyGen HD", title: "Rendered with HeyGen HD (Avatar IV / Lipsync)" },
                      };
                      const info = map[vp] || { cls: "bg-zinc-100 text-zinc-600", label: vp, title: `Rendered with ${vp}` };
                      return (
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${info.cls}`}
                          title={info.title}
                          data-testid="render-provider-badge"
                        >
                          {info.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-[400px] mx-auto">
                    <video src={result.output_url} controls className="w-full h-full object-contain" data-testid="final-video-player" />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <a
                      href={result.output_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                      className="btn-primary text-sm px-4 py-2 flex items-center gap-2 flex-1 justify-center"
                      data-testid="download-final-video"
                    >
                      <Download size={16} />
                      Download
                    </a>
                    <button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/vault/save`, {
                            title: topic,
                            platform,
                            category: "general",
                            video_url: result.output_url,
                            caption: result.caption || "",
                            hashtags: result.hashtags || "",
                            tags: tone,
                            post_type: "reel",
                            source_job_id: result.job_id,
                          });
                          alert("Saved to Content Vault!");
                        } catch (e) { alert("Error saving"); }
                      }}
                      className="text-sm px-4 py-2 flex items-center gap-2 flex-1 justify-center rounded-lg border-2 border-[#E91E8C] text-[#E91E8C] hover:bg-[#E91E8C]/10 transition-all font-semibold"
                      data-testid="save-to-vault-btn"
                    >
                      <FolderOpen size={16} />
                      Save to Vault
                    </button>
                  </div>
                  {/* One-click publish helper: caption generator + platform deep-links */}
                  <div className="mt-4">
                    <PublishPanel
                      videoUrl={result.output_url}
                      script={result.script || topic}
                      topic={topic}
                      filename={`branding-bar-${(result.job_id || "video").slice(0,8)}.mp4`}
                      compact
                    />
                  </div>
                </div>
              ) : result.status !== "completed" && (currentStep === "sync" || currentStep === "captions" || currentStep === "broll") ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex items-center gap-2 text-amber-700">
                  <CircleNotch size={16} className="animate-spin" />
                  {currentStep === "broll" ? "Generating B-Roll cutaways..." : currentStep === "captions" ? "Adding captions to your video..." : "Lip-sync is processing... This usually takes 1-3 minutes."}
                </div>
              ) : null}
            </div>
          )}

        </div>
      </div>

      {/* Past Creations — moved to full-width section below the generation
          options so users don't have to scroll past their old jobs to find
          the generate button. */}
      {jobs.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Past Creations</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="past-creations-grid">
                {jobs.map((job) => (
                  <div key={job.id} className="card overflow-hidden" data-testid={`job-${job.id}`}>
                    {/* Header - clickable to expand */}
                    <div
                      className="p-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 transition-colors"
                      onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                    >
                      <CaretRight size={14} className={`text-zinc-400 transition-transform flex-shrink-0 ${expandedJob === job.id ? "rotate-90" : ""}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">{job.topic}</p>
                        {/* Video-mode + voice badges so she can tell which render settings she liked */}
                        {(job.pipeline_mode || job.talking_photo_mode || job.voice_label) && (
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {job.pipeline_mode === "lip_sync" ? (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200" title="Rendered via Magic Hour /v1/lip-sync">
                                <VideoCamera size={9} weight="fill" /> Lip Sync
                              </span>
                            ) : job.pipeline_mode === "talking_photo" ? (
                              job.talking_photo_mode === "realistic" ? (
                                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E91E8C]/10 text-[#E91E8C] border border-[#E91E8C]/30" title="Magic Hour Talking Photo — Realistic (max likeness, slower)">
                                  Realistic
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200" title="Magic Hour Talking Photo — Prompted (2-3× faster)">
                                  <Lightning size={9} weight="fill" /> Prompted
                                </span>
                              )
                            ) : null}
                            {job.voice_label && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200" title="Voice used for this render">
                                <Microphone size={9} weight="fill" /> {job.voice_label}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-0.5">
                          <p className="text-xs text-zinc-400">
                            {new Date(job.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </p>
                          {job.status === "completed" ? (
                            job.truncation_warning ? (
                              <span className="text-xs text-amber-600 font-medium flex items-center gap-1" title={job.truncation_warning}>
                                Done <span className="text-[10px]">· ⚠ cut</span>
                              </span>
                            ) : (
                              <span className="text-xs text-green-600 font-medium">Done</span>
                            )
                          ) : job.status === "failed" ? (
                            <span className="text-xs text-red-600 font-medium">Failed</span>
                          ) : (
                            <div className="flex items-center gap-1.5 flex-1 max-w-[140px]">
                              <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-gradient-to-r from-[#E91E8C] to-[#ff6ec7] animate-pulse" style={{ width: `${getJobPct(job.current_step || "script")}%` }} />
                              </div>
                              <span className="text-[10px] font-semibold text-[#E91E8C] tabular-nums">{getJobPct(job.current_step || "script")}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {job.status === "completed" && job.output_url && (
                          <>
                            <button
                              onClick={() => handleReuseAutoCreate(job.id)}
                              className="p-2 text-zinc-500 hover:text-[#E91E8C] rounded-lg hover:bg-zinc-50"
                              title="Save as Base Video (reuse for Lip Sync)"
                              data-testid={`save-base-job-${job.id}`}
                            >
                              <FilmStrip size={16} />
                            </button>
                            <button
                              onClick={() => saveJobToVault(job)}
                              className="p-2 text-zinc-500 hover:text-[#E91E8C] rounded-lg hover:bg-zinc-50"
                              title="Save to Vault"
                              data-testid={`save-vault-job-${job.id}`}
                            >
                              <FolderOpen size={16} />
                            </button>
                            <a
                              href={job.output_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                              className="p-2 text-zinc-500 hover:text-[#E91E8C] rounded-lg hover:bg-zinc-50"
                              data-testid={`download-job-${job.id}`}
                            >
                              <Download size={16} />
                            </a>
                          </>
                        )}
                        <button
                          onClick={() => deleteJob(job.id)}
                          className="p-2 text-zinc-400 hover:text-red-500 rounded-lg hover:bg-zinc-50"
                          data-testid={`delete-job-${job.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedJob === job.id && (
                      <div className="border-t border-zinc-100 p-3 space-y-3">
                        {job.status === "failed" && Array.isArray(job.fabric_chunk_urls) && job.fabric_chunk_urls.length > 0 && (
                          <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2" data-testid={`chunk-recovery-${job.id}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                              💾 {job.fabric_chunk_urls.filter(Boolean).length} chunk{job.fabric_chunk_urls.filter(Boolean).length === 1 ? "" : "s"} were rendered on fal.ai — but stitching / rehost failed
                            </p>
                            <p className="text-[11px] text-amber-800 leading-tight">
                              Your fal.ai account was billed for these renders. Download each chunk below (URLs expire in ~24h) or forward this to support with job_id <code className="bg-white px-1 rounded text-[10px]">{job.id.slice(0, 8)}</code> to recover them.
                            </p>
                            <div className="grid grid-cols-2 gap-1.5">
                              {job.fabric_chunk_urls.map((url, i) => url ? (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[11px] font-medium px-2 py-1 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 truncate"
                                  data-testid={`chunk-recover-${job.id}-${i}`}
                                >
                                  Chunk #{i + 1} ↓
                                </a>
                              ) : (
                                <span key={i} className="text-[11px] px-2 py-1 rounded bg-zinc-100 text-zinc-400 border border-zinc-200 truncate">
                                  Chunk #{i + 1} · not rendered
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {job.status === "failed" && job.error && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid={`job-error-${job.id}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-red-700 mb-1">Error</p>
                            <p className="text-[11px] text-red-800 whitespace-pre-wrap">{job.error}</p>
                          </div>
                        )}
                        {job.truncation_warning && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`truncation-warning-${job.id}`}>
                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-1">⚠️ Script was truncated</p>
                            <p className="text-xs text-amber-800 leading-relaxed">{job.truncation_warning}</p>
                          </div>
                        )}
                        {job.status === "completed" && job.output_url && (
                          <div className="rounded-lg overflow-hidden bg-black aspect-[9/16] max-h-[350px] mx-auto">
                            <video src={job.output_url} controls className="w-full h-full object-contain" />
                          </div>
                        )}
                        {(job.caption || job.hashtags) && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Caption</p>
                            <div className="bg-[#E91E8C]/5 border border-[#E91E8C]/20 rounded-lg p-2.5 text-xs text-zinc-600 space-y-1.5">
                              {job.caption && <p className="whitespace-pre-wrap">{job.caption}</p>}
                              {job.hashtags && <p className="text-[#E91E8C] font-medium">{job.hashtags}</p>}
                            </div>
                          </div>
                        )}
                        {job.script && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Script</p>
                            <div className="bg-zinc-50 rounded-lg p-2.5 text-xs text-zinc-600 max-h-32 overflow-y-auto whitespace-pre-wrap">{job.script}</div>
                          </div>
                        )}
                        {job.audio_filename && (
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Voice</p>
                            <audio controls className="w-full h-8" src={`${BACKEND_URL}/api/voice/audio/${job.audio_filename}`} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
    </div>
  );
};

export default AutoCreate;
